import React, { useState, useEffect } from "react";
import axios from "axios";
import Slider from "react-slick";
import {
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { Edit, Cancel, Delete } from "@mui/icons-material";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useAuth } from "../AuthContext";
import "./slider-custom.css";
import loadingGif from "../loading.gif";

// Define consistent toolbar options
const toolbarOptions = [
  [{ font: [] }],
  [{ size: [] }],
  [{ color: [] }, { background: [] }],
  [{ align: [] }],
  ["bold", "italic", "underline"],
  [{ list: "ordered" }, { list: "bullet" }],
  ["link"],
  ["image"],
];

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingProject, setEditingProject] = useState(null);
  const [updatedName, setUpdatedName] = useState("");
  const [updatedDescription, setUpdatedDescription] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [expandedProject, setExpandedProject] = useState(null); // Add state for expanded project
  const { token } = useAuth();
  const [openModal, setOpenModal] = useState(false);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(
        "https://sirius.runasp.net/api/Project",
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setProjects(response.data);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, [token]);

  const handleDelete = async (id) => {
    try {
      await axios.delete(`https://sirius.runasp.net/api/Project/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setProjects(projects.filter((project) => project.id !== id));
    } catch (error) {
      console.error("There was an error deleting the project!", error);
      setError("Failed to delete the project.");
    }
  };

  const handleEdit = async (id) => {
    try {
      const formData = new FormData();
      formData.append("name", updatedName);
      formData.append("description", updatedDescription);

      if (selectedFiles.length > 0) {
        selectedFiles.forEach((file) => {
          formData.append("photos", file);
        });
      }

      await axios.put(`https://sirius.runasp.net/api/Project/${id}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      fetchProjects(); // Fetch updated projects after editing
      handleCloseModal(); // Close the modal
    } catch (error) {
      console.error(
        "There was an error updating the project!",
        error.response?.data || error.message
      );
      setError(
        `Failed to update the project. Error: ${
          error.response?.data?.message || error.message
        }`
      );
    }
  };

  const handleFileChange = (e) => {
    setSelectedFiles([...e.target.files]);
  };

  const handleOpenModal = (project) => {
    setEditingProject(project.id);
    setUpdatedName(project.name);
    setUpdatedDescription(project.description);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingProject(null);
    setSelectedFiles([]);
  };

  const handleDeletePhoto = async (fileName) => {
    try {
      await axios.delete(
        `https://sirius.runasp.net/api/Project/photo/${fileName}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      fetchProjects(); // Fetch updated projects after deleting photo
    } catch (error) {
      console.error("There was an error deleting the photo!", error);
      setError("Failed to delete the photo.");
    }
  };

  const toggleExpand = (id) => {
    setExpandedProject(expandedProject === id ? null : id);
  };

  const settings = (photoCount) => ({
    dots: 0,
    infinite: photoCount > 1,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    appendDots: (dots) => (
      <div style={{ padding: "10px" }}>
        <ul style={{ margin: "0px", padding: "0px" }}> {dots} </ul>
      </div>
    ),
    customPaging: (i) => (
      <div
        className="custom-dot"
        style={{
          width: "10px",
          height: "10px",
          background: "lightcoral",
          borderRadius: "50%",
          display: "inline-block",
          margin: "10px 5px 0 5px",
        }}
      />
    ),
    arrows: false,
  });

  if (loading) {
    return (
      <div
        style={{
          margin: "50px auto",
          textAlign: "center",
          width: "200px",
          height: "200px",
        }}
      >
        <img width={"100%"} src={loadingGif} alt="Loading..." />
      </div>
    );
  }

  if (error) return <p>Error: {error}</p>;

  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "1rem",
        flexDirection: "row",
        justifyContent: "center",
      }}
    >
      {projects.map((project) => (
        <Card key={project.id} style={{ width: "400px", marginBottom: "1rem" }}>
          <CardContent>
            <Typography variant="h6" style={{ marginBottom: "2rem" }}>
              {project.name}
            </Typography>
            <Typography
              variant="body2"
              style={{ marginBottom: "1rem", cursor: "pointer" }}
              onClick={() => toggleExpand(project.id)}
              dangerouslySetInnerHTML={{
                __html:
                  expandedProject === project.id
                    ? project.description
                    : `${project.description.substring(0, 100)}...`,
              }}
            />
            <Button
              variant="text"
              onClick={() => toggleExpand(project.id)}
              sx={{
                color: "#e84545",
                "&:hover": {
                  color: "#c73b3b",
                },
              }}
            >
              {expandedProject === project.id ? "Show Less" : "Read More"}
            </Button>
            <Slider {...settings(project.photos ? project.photos.length : 0)}>
              {project.photos &&
                project.photos.map((photo, index) => (
                  <div key={index}>
                    <img
                      src={`https://sirius.runasp.net/${photo.filePath}`}
                      alt={`Project ${index}`}
                      style={{
                        width: "100%",
                        height: "300px",
                        borderRadius: "8px",
                      }}
                    />
                  </div>
                ))}
            </Slider>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: "1rem",
              }}
            >
              <IconButton
                onClick={() => handleOpenModal(project)}
                color="primary"
                sx={{
                  color: "#e84545", // Set your desired background color here
                  "&:hover": {
                    color: "#c73b3b", // Optional: Set background color on hover
                  },
                }}
              >
                <Edit />
              </IconButton>
              <IconButton
                onClick={() => handleDelete(project.id)}
                color="error"
              >
                <Delete />
              </IconButton>
            </div>
          </CardContent>
        </Card>
      ))}

      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Project</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Project Name"
            value={updatedName}
            onChange={(e) => setUpdatedName(e.target.value)}
            style={{ marginBottom: "2rem" }}
          />
          <ReactQuill
            value={updatedDescription}
            onChange={(description, delta, source, editor) =>
              setUpdatedDescription(editor.getHTML())
            }
            theme="snow"
            modules={{ toolbar: toolbarOptions }}
            style={{ height: "200px", marginBottom: "2rem" }}
          />
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileChange}
            style={{ margin: "4rem 0 1rem 0" }}
          />
          {editingProject && (
            <div>
              <h3>Current Photos</h3>
              {projects
                .find((p) => p.id === editingProject)
                ?.photos?.map((photo, index) => (
                  <div
                    key={index}
                    style={{
                      position: "relative",
                      display: "inline-block",
                      marginRight: "1rem",
                    }}
                  >
                    <img
                      src={`https://sirius.runasp.net/${photo.filePath}`}
                      alt={`Current Photo ${index}`}
                      style={{
                        width: "100px",
                        height: "100px",
                        borderRadius: "8px",
                      }}
                    />
                    <IconButton
                      style={{
                        position: "absolute",
                        top: 0,
                        right: 0,
                        backgroundColor: "rgba(0,0,0,0.5)",
                      }}
                      onClick={() => handleDeletePhoto(photo.fileName)}
                    >
                      <Cancel style={{ color: "white", fontSize: "1rem" }} />
                    </IconButton>
                  </div>
                ))}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={() => handleEdit(editingProject)}
            color="primary"
            variant="contained"
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Projects;
